/**
 * バックエンド API の型付きクライアント。
 * 型は @mbus/shared と server/web の契約に一致させる。
 */
import type {
  StopMapItem,
  NearbyStopsResponse,
  StopDeparturesResponse,
  TripDetailResponse,
  SearchResponse,
  RtCoverageInfo,
} from '@mbus/shared';

export interface VehicleMapItem {
  vehicleId: string;
  tripId: string | null;
  routeId: string | null;
  lat: number;
  lon: number;
  bearing: number | null;
  occupancyStatus: string | null;
  stopId: string | null;
  currentStatus: string | null;
  timestamp: number | null;
}

export class ApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
  }
}

async function get<T>(path: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(path, { signal });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { message?: string };
      if (body.message) msg = body.message;
    } catch {
      /* JSON でないエラー本文は無視 */
    }
    throw new ApiError(res.status, msg);
  }
  return (await res.json()) as T;
}

const q = (params: Record<string, string | number | undefined>): string =>
  Object.entries(params)
    .filter(([, v]) => v !== undefined)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&');

// ---- 周辺停留所 (中核) ----------------------------------------------------

export function fetchNearby(
  lat: number,
  lon: number,
  radius = 800,
  signal?: AbortSignal,
): Promise<NearbyStopsResponse> {
  return get(`/api/nearby?${q({ lat, lon, radius })}`, signal);
}

// ---- 地図タイルデータ ------------------------------------------------------

export function fetchMapStops(
  b: { minLat: number; minLon: number; maxLat: number; maxLon: number },
  signal?: AbortSignal,
): Promise<{ stops: StopMapItem[]; tooWide?: boolean }> {
  return get(`/api/map/stops?${q(b)}`, signal);
}

export function fetchMapVehicles(
  b: { minLat: number; minLon: number; maxLat: number; maxLon: number },
  signal?: AbortSignal,
): Promise<{ vehicles: VehicleMapItem[]; tooWide?: boolean; generatedAt?: number }> {
  return get(`/api/map/vehicles?${q(b)}`, signal);
}

// ---- 発車情報 --------------------------------------------------------------

/** UI が扱う正規化済みの発車情報（時刻は JST ISO、分数は算出済み） */
export interface UiDeparture {
  tripUid: string;
  headsign: string;
  routeShortName: string | null;
  routeLongName: string | null;
  routeColor: string | null;
  routeTextColor: string | null;
  operatorName: string;
  /** 予定発車 (ISO8601) */
  scheduledDeparture: string;
  /** 予測/実測発車 (ISO8601)。scheduled のみなら scheduled と同値 */
  expectedDeparture: string | null;
  delaySeconds: number | null;
  minutesUntil: number;
  quality: 'realtime' | 'predicted' | 'scheduled';
  cancelled: boolean;
  occupancyStatus: string | null;
}

export interface StopDepartures {
  departures: UiDeparture[];
  generatedAt: string;
}

/** /api/departures の生レスポンス（epoch 秒・snake 寄りのフィールド） */
interface RawDeparture {
  feedId: string;
  tripId: string;
  routeShortName: string | null;
  routeLongName: string | null;
  routeColor: string | null;
  routeTextColor: string | null;
  headsign: string | null;
  operatorName: string | null;
  scheduledDeparture: number; // epoch 秒
  expectedDeparture: number; // epoch 秒
  delaySec: number | null;
  canceled: boolean;
  quality: 'realtime' | 'predicted' | 'scheduled' | 'stale';
  occupancyStatus?: string | null;
}

function normalizeDeparture(r: RawDeparture, nowSec: number): UiDeparture {
  const expSec = r.expectedDeparture ?? r.scheduledDeparture;
  return {
    tripUid: `${r.feedId}|${r.tripId}`,
    headsign: r.headsign ?? '',
    routeShortName: r.routeShortName,
    routeLongName: r.routeLongName,
    routeColor: r.routeColor,
    routeTextColor: r.routeTextColor,
    operatorName: r.operatorName ?? '',
    scheduledDeparture: new Date(r.scheduledDeparture * 1000).toISOString(),
    expectedDeparture: new Date(expSec * 1000).toISOString(),
    delaySeconds: r.delaySec,
    minutesUntil: Math.max(0, Math.round((expSec - nowSec) / 60)),
    // 'stale'（RT が古い）は時刻表相当に降格して表示する
    quality: r.quality === 'stale' ? 'scheduled' : r.quality,
    cancelled: r.canceled,
    occupancyStatus: r.occupancyStatus ?? null,
  };
}

/**
 * 発車情報。サーバはフラットな departures 配列（epoch 秒）を返すため、
 * UI 用に ISO 時刻・分数・命名を正規化して返す。stopUid は `${feedId}|${stopId}`。
 */
export async function fetchDepartures(
  stopUids: string[],
  opts: { lookahead?: number; exact?: boolean } = {},
  signal?: AbortSignal,
): Promise<StopDepartures> {
  const raw = await get<{ departures: RawDeparture[]; generatedAt: number }>(
    `/api/departures?${q({
      stops: stopUids.join(','),
      lookahead: opts.lookahead,
      // exact: 指定ポールのみ（同名・至近の兄弟ポールへ展開しない）
      group: opts.exact ? '0' : undefined,
    })}`,
    signal,
  );
  const nowSec = Date.now() / 1000;
  return {
    departures: (raw.departures ?? []).map((r) => normalizeDeparture(r, nowSec)),
    generatedAt: new Date(raw.generatedAt ?? Date.now()).toISOString(),
  };
}

// ---- 便詳細 ----------------------------------------------------------------

export interface UiTripStop {
  stopUid: string;
  seq: number;
  name: string;
  scheduledArrival: string | null;
  scheduledDeparture: string | null;
  expectedArrival: string | null;
  expectedDeparture: string | null;
  /** 車両が既に通過したか（RT 実測 or 時刻表推定） */
  passed: boolean;
  passedBasis: 'realtime' | 'inferred' | null;
  cancelled: boolean;
}

export interface UiTripDetail {
  routeShortName: string | null;
  headsign: string;
  operatorName: string;
  /** 実測の車両位置があるか（バッジ表示用） */
  hasRealtimeVehicle: boolean;
  stops: UiTripStop[];
  /** 路線形状 [lon, lat][]（MapLibre 描画用） */
  shape: [number, number][] | null;
}

interface RawTripStop {
  stopId: string;
  stopSequence: number;
  name: string;
  scheduledArrival: number | null;
  scheduledDeparture: number | null;
  expectedArrival: number | null;
  expectedDeparture: number | null;
  skipped: boolean;
}

interface RawTripDetail {
  feedId: string;
  routeShortName: string | null;
  headsign: string | null;
  operatorName: string | null;
  stops: RawTripStop[];
  shape: [number, number][] | null;
  vehicle: unknown | null;
}

const toIso = (sec: number | null): string | null =>
  sec == null ? null : new Date(sec * 1000).toISOString();

/**
 * 便詳細。サーバは epoch 秒・stopId・skipped 等を返すため UI 用に正規化する。
 * 「通過済み」はサーバが持たないため、時刻と現在時刻から決定論的に算出する
 * （RT 実測時刻があれば realtime、無ければ時刻表からの inferred）。
 */
export async function fetchTripDetail(
  tripUid: string,
  serviceDate: string,
  signal?: AbortSignal,
): Promise<UiTripDetail> {
  const i = tripUid.lastIndexOf('|');
  const feedId = tripUid.slice(0, i);
  const tripId = tripUid.slice(i + 1);
  const raw = await get<RawTripDetail>(
    `/api/trips/${encodeURIComponent(feedId)}/${encodeURIComponent(tripId)}?date=${serviceDate}`,
    signal,
  );
  const nowSec = Date.now() / 1000;
  const stops: UiTripStop[] = (raw.stops ?? []).map((s) => {
    const hasRt = s.expectedDeparture != null || s.expectedArrival != null;
    const refSec =
      s.expectedDeparture ?? s.expectedArrival ?? s.scheduledDeparture ?? s.scheduledArrival;
    const passed = refSec != null && refSec < nowSec;
    return {
      stopUid: `${feedId}|${s.stopId}`,
      seq: s.stopSequence,
      name: s.name,
      scheduledArrival: toIso(s.scheduledArrival),
      scheduledDeparture: toIso(s.scheduledDeparture),
      expectedArrival: toIso(s.expectedArrival),
      expectedDeparture: toIso(s.expectedDeparture),
      passed,
      passedBasis: passed ? (hasRt ? 'realtime' : 'inferred') : null,
      cancelled: s.skipped,
    };
  });
  return {
    routeShortName: raw.routeShortName,
    headsign: raw.headsign ?? '',
    operatorName: raw.operatorName ?? '',
    hasRealtimeVehicle: raw.vehicle != null,
    stops,
    shape: raw.shape ?? null,
  };
}

// ---- 検索 ------------------------------------------------------------------

/** /api/search の生レスポンス（停留所 + 地名を別配列で返す） */
interface RawSearchResponse {
  stops?: Array<{
    feedId: string;
    stopId: string;
    name: string;
    nameKana?: string | null;
    lat: number;
    lon: number;
    operatorName?: string | null;
  }>;
  places?: Array<{ title: string; lat: number; lon: number }>;
}

/**
 * 検索: サーバの {stops, places} を UI 用の SearchResult[] に正規化する。
 * 停留所を先（乗る場所として直接開ける）、地名を後（地図移動用）に並べる。
 */
export async function fetchSearch(text: string, signal?: AbortSignal): Promise<SearchResponse> {
  const raw = await get<RawSearchResponse>(`/api/search?q=${encodeURIComponent(text)}`, signal);
  const results: SearchResponse['results'] = [];
  for (const s of raw.stops ?? []) {
    results.push({
      kind: 'stop',
      title: s.name,
      subtitle: s.operatorName ?? null,
      lat: s.lat,
      lon: s.lon,
      stopUid: `${s.feedId}|${s.stopId}`,
    });
  }
  for (const p of raw.places ?? []) {
    results.push({
      kind: 'place',
      title: p.title,
      lat: p.lat,
      lon: p.lon,
      attribution: '国土地理院',
    });
  }
  return { results };
}

// ---- 外部リンク (東急バスナビ) ---------------------------------------------

export interface NaviLink {
  url: string;
  poles: { dpmk: string; name: string; url: string }[];
}

/**
 * 東急バス停の公式接近情報 (東急バスナビ) への直リンクを取得する。
 * 対象外フィードの停留所はサーバ側で即 null 判定 (外部アクセスなし)。
 */
export async function fetchNaviLink(
  stopUid: string,
  signal?: AbortSignal,
): Promise<NaviLink | null> {
  const i = stopUid.lastIndexOf('|');
  if (i <= 0) return null;
  const res = await get<{ link: NaviLink | null }>(
    `/api/stops/navi-link?${q({ feedId: stopUid.slice(0, i), stopId: stopUid.slice(i + 1) })}`,
    signal,
  );
  return res.link;
}

// ---- 出発ウォッチ (Web Push) ------------------------------------------------

export function fetchPushKey(): Promise<{ publicKey: string }> {
  return get('/api/push/key');
}

export async function createWatch(body: {
  subscription: PushSubscriptionJSON;
  feedId: string;
  tripId: string;
  stopId: string;
  serviceDate: string;
  leadMinutes: number;
}): Promise<{ watchId: string }> {
  const res = await fetch('/api/watches', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new ApiError(res.status, `watch failed: HTTP ${res.status}`);
  return (await res.json()) as { watchId: string };
}

export type { StopMapItem, NearbyStopsResponse, StopDeparturesResponse, TripDetailResponse, RtCoverageInfo };
